import React, { useEffect, useState } from 'react';

import { Button, Loader } from '@els/els-react--leyden';

import useAuth from 'hooks/useAuth';

const AUTH_LOADING = 'auth-loading';
const MUTED_COLOR = 'var(--muted)';

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, isSessionExpired, login } = useAuth();
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !isSessionExpired) {
      login().catch((err: unknown) => {
        setLoginError(err instanceof Error ? err.message : 'Failed to start login');
      });
    }
  }, [isLoading, isAuthenticated, isSessionExpired, login]);

  if (loginError) {
    return (
      <div className={AUTH_LOADING}>
        <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '480px' }}>
          <p style={{ color: 'var(--r)', marginBottom: '0.5rem' }}>SSO login failed</p>
          <p style={{ color: MUTED_COLOR, fontSize: '0.875rem' }}>{loginError}</p>
          <p style={{ color: MUTED_COLOR, fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Ensure <code>REACT_APP_AUTH_AUTHORITY</code> and <code>REACT_APP_AUTH_CLIENT_ID</code> are set in <code>.env</code> and the dev server was restarted.
          </p>
        </div>
      </div>
    );
  }

  if (isSessionExpired) {
    return (
      <div className={AUTH_LOADING}>
        <div style={{ textAlign: 'center', padding: '2rem', maxWidth: '480px' }}>
          <p style={{ color: 'var(--r)', marginBottom: '0.5rem' }}>Session expired</p>
          <p style={{ color: MUTED_COLOR, fontSize: '0.875rem', marginBottom: '1rem' }}>Your session has expired. Please log in again to continue.</p>
          <Button
            onClick={() => {
              login().catch((err: unknown) => {
                setLoginError(err instanceof Error ? err.message : 'Failed to start login');
              });
            }}
          >
            Log in again
          </Button>
        </div>
      </div>
    );
  }

  // Show loader while initial auth check is running OR while redirect to IdP is pending.
  // Never return null — that causes a blank page.
  if (isLoading || !isAuthenticated) {
    return (
      <div className={AUTH_LOADING}>
        <Loader isVisible isBlocking={false} />
      </div>
    );
  }

  return children as React.ReactElement;
};

export default RequireAuth;
